/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file appBadge.js
 * @description
 * Single owner of the APP ICON badge.
 *
 * The badge shows `unread in-app notifications + unread incoming e-mails` — the
 * same two numbers the Notifications inbox header and the Mail home header render.
 * Before this module the badge was mail-only and was refreshed from exactly two
 * places (a notification-shade action and the pending-action flush), so the icon
 * drifted from reality as soon as anything else changed.
 *
 * Design notes:
 *  - ONE cheap backend call (`GET /api/notifications/badge`) returns both counters.
 *    A backend older than that endpoint (404) falls back to the two per-source
 *    calls, so the app stays correct against a not-yet-deployed server.
 *  - Every refresh is de-duplicated: concurrent callers share the in-flight promise
 *    and a short cooldown collapses the bursts that a foreground + poll + push
 *    arriving together would otherwise produce.
 *  - Nothing here ever throws or logs a user-visible error. A wrong badge is a
 *    cosmetic problem; a crash in a headless notification task is not.
 *  - No-ops on web (`Notifications.setBadgeCountAsync` is unavailable there).
 */

import * as Notifications from 'expo-notifications';
import {Platform} from 'react-native';
import {getNotificationBadgeCounts, listNotifications} from '../services/notificationApi';

/** Minimum spacing between two real refreshes; bursts inside it reuse the last result. */
const BADGE_REFRESH_COOLDOWN_MS = 4000;

/** Hard ceiling — iOS renders anything larger as a useless blob. */
const BADGE_MAX_COUNT = 999;

let inFlight = null;
let lastAppliedAt = 0;
let lastAppliedTotal = null;
let badgeEndpointMissing = false;

/**
 * Last counters read from the server. Also feeds the in-app notification bell, so
 * the badge on the icon and the badge in every header come from ONE fetch.
 * @type {{notifications: number, mail: number, total: number}}
 */
let lastCounts = {notifications: 0, mail: 0, total: 0};
const countsListeners = new Set();

/**
 * Publish `lastCounts` to every subscriber. Listener errors are contained so one
 * broken consumer can never break the badge or its siblings.
 *
 * @returns {void}
 */
const emitCounts = () => {
    countsListeners.forEach((listener) => {
        try {
            listener(lastCounts);
        } catch (_e) {
            // a subscriber must never break the badge pipeline
        }
    });
};

/**
 * Current unread counters (last successful read). Safe to call before any refresh —
 * it then reports zeros.
 *
 * @returns {{notifications: number, mail: number, total: number}}
 */
export const getAppBadgeCounts = () => lastCounts;

/**
 * Subscribe to unread-counter changes (the in-app bell badge).
 *
 * The listener fires immediately with the current counters, then on every refresh
 * that produced new numbers — no extra requests, the icon badge's own fetch is
 * what feeds it.
 *
 * @param {(counts: {notifications: number, mail: number, total: number}) => void} listener
 * @returns {() => void} Unsubscribe function.
 */
export const subscribeAppBadgeCounts = (listener) => {
    if (typeof listener !== 'function') {
        return () => {};
    }
    countsListeners.add(listener);
    try {
        listener(lastCounts);
    } catch (_e) {
        // ignore — see emitCounts
    }

    return () => {
        countsListeners.delete(listener);
    };
};

/**
 * True when this platform can render an app icon badge at all.
 *
 * @returns {boolean}
 */
const badgeSupported = () => Platform.OS !== 'web'
    && typeof Notifications?.setBadgeCountAsync === 'function';

/**
 * Coerce an API counter into a safe non-negative integer.
 *
 * @param {*} value
 * @returns {number}
 */
const toCount = (value) => {
    const parsed = Number(value);

    return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 0;
};

/**
 * Fetch both unread counters, preferring the single-call badge endpoint.
 *
 * @returns {Promise<?{notifications: number, mail: number, total: number}>} null when
 *     neither source could be read (offline, logged out) — the badge is then left alone.
 */
const fetchCounts = async () => {
    if (!badgeEndpointMissing) {
        try {
            const payload = await getNotificationBadgeCounts();
            const notifications = toCount(payload?.notifications);
            const mail = toCount(payload?.mail);

            return {
                notifications,
                mail,
                total: toCount(payload?.total) || (notifications + mail),
            };
        } catch (e) {
            // 404/405 = backend without the endpoint yet. Remember it so we stop paying
            // for a failing round trip on every refresh; any other error just falls
            // through to the per-source path for this one call.
            if (e?.status === 404 || e?.status === 405) {
                badgeEndpointMissing = true;
            } else if (e?.code === 'AUTH_REQUIRED') {
                return null;
            }
        }
    }

    // Fallback: the two sources separately. `read:'false'` + `limit:1` is the same
    // 1-row unread probe NotificationsScreen uses — we only read the envelope `total`.
    //
    // `mailApi` is required LAZILY, only on this rare path: this module is imported by
    // every header that shows the bell, and a static import would drag the whole mail
    // client (and expo-file-system) into their module graph.
    const [notificationsResult, mailResult] = await Promise.allSettled([
        listNotifications({page: 1, limit: 1, read: 'false'}),
        import('../services/mailApi').then((mod) => mod.getMailStatus()),
    ]);

    if (notificationsResult.status === 'rejected' && mailResult.status === 'rejected') {
        return null;
    }

    const notifications = notificationsResult.status === 'fulfilled'
        ? toCount(notificationsResult.value?.total)
        : 0;
    const mail = mailResult.status === 'fulfilled'
        ? toCount(mailResult.value?.inbox?.unreadIncomingCount ?? mailResult.value?.inbox?.unreadCount)
        : 0;

    return {notifications, mail, total: notifications + mail};
};

/**
 * Write a value straight onto the app icon, bypassing every counter fetch.
 * Used on logout and after "mark all as read"-style local certainty.
 *
 * @param {number} count Desired badge value.
 * @returns {Promise<boolean>} true when the badge was written.
 */
export const setAppBadgeCount = async (count) => {
    if (!badgeSupported()) {
        return false;
    }
    try {
        const next = Math.min(toCount(count), BADGE_MAX_COUNT);
        await Notifications.setBadgeCountAsync(next);
        lastAppliedTotal = next;
        lastAppliedAt = Date.now();

        return true;
    } catch (_e) {
        return false;
    }
};

/**
 * Clear the app icon badge (logout / session end).
 *
 * @returns {Promise<boolean>}
 */
export const clearAppBadge = async () => {
    // Also forget the cached total, so the next login's first refresh is never
    // skipped by the "unchanged" short-circuit in {@link refreshAppBadge}.
    lastAppliedTotal = null;
    lastCounts = {notifications: 0, mail: 0, total: 0};
    emitCounts();

    return setAppBadgeCount(0);
};

/**
 * Recompute the app icon badge from the server and apply it.
 *
 * Safe to call from anywhere and as often as convenient: concurrent calls share one
 * request, calls inside the cooldown window are dropped, and the badge is only
 * written when the number actually changed. Never throws.
 *
 * @param {{force?: boolean}} [options] `force: true` ignores the cooldown (use after
 *     an explicit user action such as mark-all-read, not for polling).
 * @returns {Promise<?number>} The applied total, or null when nothing was applied.
 */
export const refreshAppBadge = async (options = {}) => {
    if (inFlight) {
        return inFlight;
    }
    if (!options.force && lastAppliedTotal !== null && Date.now() - lastAppliedAt < BADGE_REFRESH_COOLDOWN_MS) {
        return lastAppliedTotal;
    }

    inFlight = (async () => {
        try {
            const counts = await fetchCounts();
            if (!counts) {
                return null;
            }
            // Publish first: the in-app bell badge works on web too, where the OS
            // icon badge does not exist.
            if (counts.notifications !== lastCounts.notifications
                || counts.mail !== lastCounts.mail
                || counts.total !== lastCounts.total) {
                lastCounts = counts;
                emitCounts();
            }
            const next = Math.min(counts.total, BADGE_MAX_COUNT);
            lastAppliedAt = Date.now();
            if (!badgeSupported() || next === lastAppliedTotal) {
                return next;
            }
            await Notifications.setBadgeCountAsync(next);
            lastAppliedTotal = next;

            return next;
        } catch (_e) {
            return null;
        } finally {
            inFlight = null;
        }
    })();

    return inFlight;
};

/**
 * Drop the cached badge state. Called on logout/login so a new session never
 * inherits the previous user's counters or the "endpoint missing" verdict.
 *
 * @returns {void}
 */
export const resetAppBadgeState = () => {
    lastAppliedTotal = null;
    lastAppliedAt = 0;
    badgeEndpointMissing = false;
    lastCounts = {notifications: 0, mail: 0, total: 0};
    emitCounts();
};
